import {
    Calendar as CalendarSVG,
    GhostGraphic as GhostSVG,
    Heart as HeartSVG,
    TicketsIllustration as EmptyStateSVG,
} from '@eventbrite/legacy-eds.iconography';
import { defineMessages, MessageDescriptor } from 'react-intl';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import React, { ReactElement } from 'react';
import { StateMessage } from '../StateMessage';

export interface EmptyStateConfig {
    title: MessageDescriptor;
    iconType?: string;
    iconSize?: string;
    description?: MessageDescriptor;
    primaryLink?: string;
    primaryText?: MessageDescriptor;
    buttonStyle?: string;
}

export interface EmptyStateShape {
    validation: {
        isAuthenticated?: boolean;
        useDefault?: boolean;
        shouldShowEmptyState?: boolean;
    };
    config: EmptyStateConfig;
}

export type EmptyStates = EmptyStateShape[];

const messages = defineMessages({
    noEvents: {
        id: 'No events in your area',
        defaultMessage: 'No events in your area',
    },
    tryDifferentLocation: {
        id: 'Try a different location',
        defaultMessage: 'Try a different location',
    },
});

export const USE_DEFAULT_EMPTY_STATE: EmptyStateConfig = {
    title: messages.noEvents,
    iconSize: 'medium',
    description: messages.tryDifferentLocation,
};

const EmptyStateIconMap: Record<string, ReactElement> = {
    EmptyState: <EmptyStateSVG />,
    Heart: <HeartSVG />,
    Ghost: <GhostSVG />,
};

export interface EmptyStateProps {
    emptyState?: EmptyStates;
    bucketKey?: string;
    isAuthenticated?: boolean;
}

const _getEmptyStateComponent = ({
    bucketKey,
    title,
    iconSize,
    description,
    primaryLink,
    primaryText,
    buttonStyle,
    iconType = 'default',
}: {
    bucketKey?: string;
    title?: MessageDescriptor;
    iconType?: string;
    iconSize?: string;
    description?: MessageDescriptor;
    primaryLink?: string;
    primaryText?: MessageDescriptor;
    buttonStyle?: string;
}) => (
    <>
        <div
            data-testid="feed-events-empty"
            className="feed-events__empty-state"
            key={bucketKey}
            id={`feed-events__empty-state-${bucketKey}`}
        >
            <StateMessage
                key={`empty-state-${bucketKey}-isAuth`}
                title={title}
                iconType={EmptyStateIconMap?.[iconType] || <CalendarSVG />}
                iconSize={iconSize}
                description={description}
                primaryLink={primaryLink}
                primaryText={primaryText}
                buttonStyle={buttonStyle}
            />
        </div>
    </>
);

export const EmptyState = (props: EmptyStateProps) => {
    const { emptyState, bucketKey, isAuthenticated } = props;

    // Default behavior is don't show any bucket
    let emptyStateComponent = null;

    // Get the first valid condition for empty state
    const validEmptyState = find(
        emptyState,
        (es: EmptyStateShape) =>
            es.validation.shouldShowEmptyState &&
            (es.validation.isAuthenticated === isAuthenticated ||
                es.validation.useDefault === true),
    );

    // If we don't have a valid condition is because we don't have to render any bucket
    if (!isEmpty(validEmptyState)) {
        let emptyStateConfig: EmptyStateConfig = USE_DEFAULT_EMPTY_STATE;

        // If we have a config, we overwrite default empty state
        if (!isEmpty((validEmptyState as EmptyStateShape)?.config)) {
            emptyStateConfig = (validEmptyState as EmptyStateShape)?.config;
        }
        emptyStateComponent = _getEmptyStateComponent({
            bucketKey,
            ...emptyStateConfig,
        });
    }

    return emptyStateComponent;
};
